/**
 * Domain types mirroring backend/src/shared/types.ts. Kept as a hand-synced
 * copy (no cross-package import) since frontend and backend build/deploy
 * independently and the frontend must never depend on backend source paths.
 */

export type CustomerStatus = 'not_opened' | 'in_progress' | 'submitted' | 'updated_after_submission';

export interface Customer {
  customerId: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  notes?: string;
  /** Question IDs this customer's link should show; unset means "all questions". */
  assignedQuestionIds?: string[];
  /** Public CloudFront URL of the customer's uploaded logo, if any. */
  logoUrl?: string;
  status: CustomerStatus;
  retentionDate?: string;
  archived: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type QuestionType = 'short_text' | 'long_text' | 'single_choice' | 'multiple_choice' | 'number' | 'yes_no';

/** Built-in starting groups, offered as suggestions - admins can add more by typing a new name. */
export const DEFAULT_QUESTION_GROUPS = ['AWS Migration', 'SAP', 'SAP Conversion', 'RISE', 'Ariba', 'ABAP'] as const;

export interface Question {
  questionId: string;
  text: string;
  helpText?: string;
  type: QuestionType;
  options?: string[];
  required: boolean;
  priorityGroup: string;
  category: string;
  /** Product-line grouping (AWS Migration/SAP/etc.); free text, missing means "AWS Migration". */
  group?: string;
  order: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AnswerValue = string | number | boolean | string[];

export interface Submission {
  customerId: string;
  version: number;
  answers: Record<string, AnswerValue>;
  submittedAt: string;
  isDraft: boolean;
}

export interface DashboardRow {
  customerId: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  status: CustomerStatus;
  changedAfterFirstSubmission: boolean;
  lastActivityAt?: string;
}

export interface DashboardResponse {
  rows: DashboardRow[];
  filters: { categories: string[]; priorityGroups: string[] };
}

export interface QuestionHistoryEntry {
  version: number;
  submittedAt: string;
  isDraft: boolean;
  value: AnswerValue | undefined;
  changed: boolean;
}

export interface QuestionHistory {
  questionId: string;
  text: string;
  category: string;
  priorityGroup: string;
  entries: QuestionHistoryEntry[];
}

export interface LinkStatus {
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  active: boolean;
}

export type Role = 'Admin' | 'ReadOnly';

/** Public-facing question shape (no admin-only fields like archived/createdAt). */
export interface PublicQuestion {
  questionId: string;
  text: string;
  helpText?: string;
  type: QuestionType;
  options?: string[];
  required: boolean;
  priorityGroup: string;
  category: string;
  group?: string;
  order: number;
}

export interface PublicLinkResponse {
  companyName: string;
  logoUrl?: string;
  expiresAt: string;
  questions: PublicQuestion[];
  answers: Record<string, AnswerValue>;
  isDraft: boolean;
  submittedAt?: string;
}
